import React, {useRef, useEffect, useCallback, useReducer, useState} from "react";
import {Box, Flex, Skeleton, useDisclosure} from "@chakra-ui/react";
import useUserStore from "../store/useUserStore.ts";
import {useProjectsStore} from "../store/useProjectsStore.ts";
import FeedbackDrawer from "../components/FeedbackDrawer/FeedbackDrawer.tsx";
import TranslationQCDrawer from "../components/TranslationQCDrawer/TranslationQCDrawer.tsx";
import DeleteProjectAlertDialog from "../components/projectsTable/DeleteProjectAlertDialog.tsx";
import {ONE_MINUTE_IN_ML} from "../models/constants/constants.ts";
import MainTableActions from "../components/projectsTable/MainTableActions.tsx";
import EmptyProjectTableView from "../components/projectsTable/EmptyProjectTableView.tsx";
import PreviewWithQCSession from "../components/PreviewWithQCSession/PreviewWithQCSession.tsx";
import {useProjectUploader} from "../hooks/useProjectUploader.ts";
import {useProjectPolling} from "../hooks/useProjectPolling";
import {usePagination} from "../hooks/usePagination.ts";
import ProjectTable from "../components/projectsTable/ProjectTable.tsx";
import PaginationControls from "../components/projectsTable/PaginationControls.tsx";
import {
  mainPageReducer,
  initialMainPageState,
} from "../reducers/mainPageReducer";
import {IProject} from "../models/interfaces/project.interface.ts";
import {useProject} from "../hooks/useProject.ts";
import {useCreateNewProjectModal} from "../hooks/useCreateNewProjectModal.tsx";
import {ICreateNewProjectState} from "../reducers/createNewProjectReducer.ts";
import useUiStore from "../store/useUiStore.ts";
import ProjectDrawer from "../components/ProjectDetails/ProjectDrawer.tsx";
import {PreviewQCProvider} from "../context/PreviewQCContext.tsx";
import {usePreviewQC} from "../context/usePreviewQC.ts";
import {sortProjectsNewestFirst} from "../utils/projectOrdering.ts";

const calculateItemsPerPage = () => {
  const screenHeight = window.innerHeight;

  if (screenHeight >= 1080) return 8;  // large external screen
  if (screenHeight >= 900) return 7;   // medium resolution
  return 6;                            // smaller laptops
};

const MainPageContent = () => {
  const {resetSession} = usePreviewQC();
  const {closeModal, openDrawer} = useUiStore();
  const {getLanguageConfig, getUserPreferences} = useUserStore();
  const {projects, projectsToView, getProjects, refreshProjectsSilently} = useProjectsStore();
  const {isBulkExportReady} = useProject()
  const {triggerUpload} = useProjectUploader();
  useProjectPolling(refreshProjectsSilently, ONE_MINUTE_IN_ML);
  const [state, dispatch] = useReducer(mainPageReducer, initialMainPageState);
  const {
    isOpen: isAlertDialogOpen,
    onOpen: onAlertDialogOpen,
    onClose: onAlertDialogClose,
  } = useDisclosure();
  const [itemsPerPage, setItemsPerPage] = useState(calculateItemsPerPage());
  const tableRef = useRef<HTMLDivElement>(null);
  const showCreateNewProjectModal: (handleCreateProject: (state: ICreateNewProjectState) => Promise<void>) => void = useCreateNewProjectModal()

  const closeQCSession = useCallback(() => {
    resetSession();
    dispatch({type: "SET_PREVIEW_MODAL", payload: false});
    dispatch({type: "SET_PREVIEW_PROJECT_ID", payload: null});
    dispatch({type: "SET_TRANSLATION_QC_DRAWER", payload: false});
    dispatch({type: "SET_TRANSLATION_QC_PROJECT_ID", payload: null});
    dispatch({type: "SET_QC_OPENED_WITH_PREVIEW", payload: false});
  }, [resetSession]);

  useEffect(() => {
    const handleResize = () => {
      setItemsPerPage(calculateItemsPerPage());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch projects initially
  useEffect(() => {
    getProjects().then(() => {
      dispatch({type: "SET_GET_PROJECT_TRIGGERED", payload: true});
    });
  }, [getProjects]);

  useEffect(() => {
    getLanguageConfig().then(() => null);
    getUserPreferences().then(() => null);
  }, [getLanguageConfig, getUserPreferences]);

  const handleCreateProject = async (
    state: ICreateNewProjectState
  ): Promise<void> => {
    closeModal();
    triggerUpload(state).catch((error: Error) => console.error(error));
  };

  const onCreateNewProjectClicked = async (): Promise<void> => {
    showCreateNewProjectModal(handleCreateProject)
  };

  const openFeedbackDrawer = (projectId: string, targetLanguageCodes: string[]) => {
    dispatch({type: "SET_FEEDBACK_PROJECT_ID", payload: projectId})
    dispatch({type: "SET_FEEDBACK_TARGET_LANGUAGES", payload: targetLanguageCodes})
    dispatch({type: "SET_SHOW_FEEDBACK_DRAWER", payload: true})
  };

  const openTranslationQCDrawer = useCallback((projectId: string) => {
    resetSession();
    dispatch({type: "SET_PREVIEW_MODAL", payload: false});
    dispatch({type: "SET_PREVIEW_PROJECT_ID", payload: null});
    dispatch({type: "SET_QC_OPENED_WITH_PREVIEW", payload: false});
    dispatch({type: "SET_TRANSLATION_QC_PROJECT_ID", payload: projectId});
    dispatch({type: "SET_TRANSLATION_QC_DRAWER", payload: true});
  }, [resetSession]);

  const toggleSelectedRow = (project: IProject): void => {
    const selectedProjects = [...state.selectedRows];
    if (selectedProjects.includes(project.id)) {
      dispatch({
        type: "SET_SELECTED_ROWS",
        payload: selectedProjects.filter((projectId: string) => projectId !== project.id)
      })
    } else {
      dispatch({type: "SET_SELECTED_ROWS", payload: [...selectedProjects, project.id]})
    }
  };


  const selectAllCompletedProjects = (projects: IProject[]): void => {
    dispatch({
      type: "SET_SELECTED_ROWS",
      payload: projects
        .filter((project: IProject) => isBulkExportReady(project))
        .map((project: IProject) => project.id)
    })
  };

  const presentPreview = useCallback((projectId: string) => {
    resetSession();
    dispatch({type: "SET_TRANSLATION_QC_DRAWER", payload: false});
    dispatch({type: "SET_TRANSLATION_QC_PROJECT_ID", payload: null});
    dispatch({type: "SET_PREVIEW_PROJECT_ID", payload: projectId});
    dispatch({type: "SET_PREVIEW_MODAL", payload: true});
    dispatch({type: "SET_QC_OPENED_WITH_PREVIEW", payload: true});
  }, [resetSession]);

  const {currentPage, setCurrentPage, totalPages, paginatedIndices} = usePagination(
    projects.length,
    itemsPerPage
  );

  const paginatedProjects: IProject[] = sortProjectsNewestFirst(
    projects.filter((project: IProject) => {
      return projectsToView.length > 0 ? projectsToView.includes(project.id) : true;
    })
  ).slice(paginatedIndices.start, paginatedIndices.end);

  const openProjectDrawer = (projectId: string) => {
    openDrawer(<ProjectDrawer projectId={projectId}/>)
  }

  return (
    <>
      {/* Projects Table */}
      <Flex className="project-table-wrapper">
        <MainTableActions
          selectedRows={state.selectedRows}
          setSelectedRows={(selectedRows: string[]) => dispatch({type: "SET_SELECTED_ROWS", payload: selectedRows})}
          isDownloading={state.isDownloading}
          setIsDownloading={(update) =>
            dispatch({
              type: "SET_IS_DOWNLOADING",
              payload: typeof update === "function" ? update(state.isDownloading) : update,
            })
          }
          selectAllCompletedProjects={() => selectAllCompletedProjects(projects)}
          onCreateNewProjectClicked={onCreateNewProjectClicked}
        />
        <Skeleton isLoaded={state.isGetProjectTriggered} className="mt-4">
          <Box minH={`${itemsPerPage * 88}px`} className="w-full">
            {projects.length == 0
              ? <EmptyProjectTableView onCreateNewProjectClicked={onCreateNewProjectClicked}/>
              : (
                <>
                  <ProjectTable
                    tableRef={tableRef}
                    projects={paginatedProjects}
                    selectedRows={state.selectedRows}
                    toggleSelectedRow={toggleSelectedRow}
                    setSelectedProjectId={(id: string | null) => dispatch({
                      type: "SET_SELECTED_PROJECT_ID",
                      payload: id
                    })}
                    onAlertDialogOpen={onAlertDialogOpen}
                    showProjectOverview={(projectId: string) => openProjectDrawer(projectId)}
                    openFeedbackDrawer={openFeedbackDrawer}
                    openTranslationQCDrawer={openTranslationQCDrawer}
                    presentPreview={presentPreview}
                    isDownloading={state.isDownloading}
                    setIsDownloading={(update) =>
                      dispatch({
                        type: "SET_IS_DOWNLOADING",
                        payload: typeof update === "function" ? update(state.isDownloading) : update,
                      })
                    }
                  />
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                    hoveredButton={state.hoveredButton}
                    setHoveredButton={(hovered: string | null) => dispatch({
                      type: "SET_HOVERED_BUTTON",
                      payload: hovered
                    })}
                  />
                </>
              )
            }
          </Box>
        </Skeleton>
      </Flex>

      <FeedbackDrawer
        projectId={state.feedbackProjectId}
        targetLanguageCodes={state.feedbackTargetLanguages}
        maxRowNumber={
          state.feedbackProjectId
            ? projects.find((p: IProject) => p.id === state.feedbackProjectId)?.mediaMeta.amountRows ?? 0
            : 0
        }
        isOpen={state.isFeedbackDrawerOpen}
        onClose={() => dispatch({type: "SET_SHOW_FEEDBACK_DRAWER", payload: false})}
      />
      {state.qcOpenedWithPreview && <PreviewWithQCSession isOpen={state.isPreviewModalOpen} onClose={closeQCSession} projectId={state.previewProjectId} />}
      <TranslationQCDrawer projectId={state.translationQCProjectId} isOpen={state.isTranslationQCDrawerOpen} onClose={closeQCSession} />

      <DeleteProjectAlertDialog
        isOpen={isAlertDialogOpen}
        onClose={onAlertDialogClose}
        selectedProjectId={state.selectedProjectId}
        setSelectedProjectId={(id: string | null) => dispatch({type: "SET_SELECTED_PROJECT_ID", payload: id})}
      />

    </>
  );
};

const MainPage = () => <PreviewQCProvider><MainPageContent /></PreviewQCProvider>;

export default MainPage;
